// uPlot 의 paths 자리에 끼우는 막대·면 그리기.
//
// uPlot 에도 막대(uPlot.paths.bars)가 있는데 따로 두는 이유: 칸이 1px 보다 좁을 때 그쪽은
// 버킷 수만큼 사각형을 겹쳐 그리고, 쌓은 막대는 조각마다 다른 버킷의 최댓값이 섞인다.
// 여기서는 model.js 의 binColumns 로 **픽셀 열마다 대표 버킷 하나**를 골라 그 버킷의
// 조각만 쌓는다(근거는 binColumns 머리말). 모양을 정하는 계산은 전부 model.js 에 있고
// node --test 가 본다 — 이 파일은 그 결과를 Path2D 로 옮기기만 한다.
import { binColumns, pickKind, BAR_GAP } from './model.js'

// columnPaths 는 시리즈 하나의 paths 함수를 만든다.
//
//   meta.kind        'bar' | 'area' | 'auto'
//   meta.bucketMs    버킷 폭(ms)
//   meta.base        이 시리즈의 바닥(누적값) 배열. 안 쌓으면 null
//   meta.groupTop    묶음 맨 위 누적값 배열 — 열의 대표를 이것으로 고른다(묶음 안 모든 조각이
//                    같은 대표를 써야 한 막대가 한 시각이 된다)
//   meta.colors()    { fill, over } — 지금 테마로 푼 색. over 는 { value, fill } | null
export function columnPaths(meta) {
  return (u, si, i0, i1) => {
    const xs = u.data[0]
    const tops = u.data[si]
    const { min, max } = u.scales.x
    const plotPx = u.bbox.width
    const kind = pickKind(meta.kind, meta.bucketMs, { from: min, to: max }, plotPx / devicePixelRatioOf(u))
    const colPx = (meta.bucketMs / ((max - min) || 1)) * plotPx
    const cols = binColumns({
      xs, tops: meta.groupTop, i0, i1,
      toPx: (t) => u.valToPos(t, 'x', true),
      colPx,
      gap: kind === 'bar' ? BAR_GAP : 0,
    })

    const { fill, over } = meta.colors()
    const main = new Path2D()
    // 기준 색이 본색과 같으면 가를 까닭이 없다(Map 열쇠가 겹쳐 한쪽이 사라지기도 한다).
    const hot = over && over.fill !== fill ? new Path2D() : null
    for (const c of cols) {
      const top = tops[c.i]
      if (top == null) continue
      const b = meta.base ? (meta.base[c.i] ?? 0) : 0
      if (top === b) continue
      const yTop = u.valToPos(top, 'y', true)
      const yBase = u.valToPos(b, 'y', true)
      const path = hot && top - b >= over.value ? hot : main
      path.rect(c.x, Math.min(yTop, yBase), c.w, Math.abs(yBase - yTop))
    }

    // fill 에 Map 을 주면 uPlot 이 색마다 한 번씩 칠한다 — 기준을 넘은 막대만 다른 색으로
    // 칠하는 데 쓴다(디스크 포화 같은 것). 색이 막대마다 달라져도 사각형을 두 번 만들지 않는다.
    const fillPath = hot ? new Map([[fill, main], [over.fill, hot]]) : main
    return { stroke: null, fill: fillPath, clip: null, band: null, gaps: null, flags: 0 }
  }
}

// uPlot 은 캔버스 좌표를 device px 로 준다. auto 판단은 CSS px 기준(AUTO_BAR_MIN_PX)이라 되돌린다.
export function devicePixelRatioOf(u) {
  const css = u.over?.clientWidth
  return css > 0 ? u.bbox.width / css : (globalThis.devicePixelRatio || 1)
}
